import { useState, useEffect, useRef, Fragment } from 'react'
import api from '../services/api'

const TIPOS = [
  { value: 'NFE', label: 'NF-e' },
  { value: 'CTEOS', label: 'CT-e OS' },
  { value: 'NFSE', label: 'NFS-e (Municipal e Nacional)' },
]

interface Pagamento { data: string; portador_cod: number; portador_des: string; valor: number }
interface Duplicata { dpv_numero: string; vencimento: string | null; valor_liquido: number; recebido: number; saldo: number; pagamentos: Pagamento[] }
interface Nota {
  ntv_empcod: number; ntv_serie: string; ntv_numero: number; cli_cod: string; cliente: string
  ftv_tipdco: string; ntv_datemi: string
  qtd_parcelas: number; primeiro_vencto: string | null
  valor: string; retido: string; financeiro: string; recebido: string
  duplicatas?: Duplicata[]
}
interface Empresa { empcod: number; empnom: string; empfan: string; empcgc: string }

function hoje() { return new Date().toISOString().slice(0, 10) }
function primeiroDiaMes() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01` }

function fmtMoeda(v: string | number) {
  return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function fmtData(v: string | null) {
  return v ? String(v).slice(0, 10).split('-').reverse().join('/') : '-'
}

// ── Combo com checkboxes (multi-seleção) ──────────────────────────────────────
function ComboCheck({ label, opcoes, selecionados, onChange, placeholder, width }: {
  label: string
  opcoes: { value: string; label: string }[]
  selecionados: string[]
  onChange: (v: string[]) => void
  placeholder: string
  width?: number
}) {
  const [aberto, setAberto] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  function toggle(v: string) {
    onChange(selecionados.includes(v) ? selecionados.filter(x => x !== v) : [...selecionados, v])
  }

  const resumo = selecionados.length === 0
    ? placeholder
    : selecionados.length === opcoes.length
      ? 'Todas'
      : selecionados.length === 1
        ? opcoes.find(o => o.value === selecionados[0])?.label ?? '1 selecionada'
        : `${selecionados.length} selecionadas`

  return (
    <div className="field" style={{ position: 'relative', width: width ?? 200, flex: '0 0 auto' }} ref={ref}>
      <label>{label}</label>
      <button
        type="button"
        onClick={() => setAberto(o => !o)}
        style={{
          width: '100%', textAlign: 'left', background: '#fff', border: '1px solid #d1d5db',
          borderRadius: 6, padding: '0.45rem 0.6rem', fontSize: '0.85rem', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          color: selecionados.length ? '#111827' : '#9ca3af',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resumo}</span>
        <span style={{ color: '#9ca3af', marginLeft: 6 }}>{aberto ? '▴' : '▾'}</span>
      </button>

      {aberto && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 2,
          background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
          maxHeight: 260, overflowY: 'auto', boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', borderBottom: '1px solid #eee', background: '#fafafa' }}>
            <button type="button" onClick={() => onChange(opcoes.map(o => o.value))}
              style={{ fontSize: '0.72rem', color: '#1a6ea8', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
              Marcar todas
            </button>
            <button type="button" onClick={() => onChange([])}
              style={{ fontSize: '0.72rem', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}>
              Limpar
            </button>
          </div>
          {opcoes.map(o => (
            <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', fontSize: '0.82rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={selecionados.includes(o.value)} onChange={() => toggle(o.value)}
                style={{ width: 15, height: 15, flexShrink: 0, margin: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

export default function RelatorioContasReceber() {
  const [dataIni, setDataIni] = useState(primeiroDiaMes())
  const [dataFim, setDataFim] = useState(hoje())
  const [empresas, setEmpresas] = useState<Empresa[]>([])
  const [empresasSel, setEmpresasSel] = useState<string[]>([])
  const [tiposSel, setTiposSel] = useState<string[]>([])

  const [buscaCliente, setBuscaCliente] = useState('')
  const [clienteSel, setClienteSel] = useState<{ cli_cod: string; nome: string } | null>(null)
  const [sugestoesCliente, setSugestoesCliente] = useState<{ cli_cod: string; cli_nom: string; cli_fan: string }[]>([])

  const [carregando, setCarregando] = useState(false)
  const [gerandoPdf, setGerandoPdf] = useState(false)
  const [gerandoExcel, setGerandoExcel] = useState(false)
  const [erro, setErro] = useState('')
  const [notas, setNotas] = useState<Nota[] | null>(null)

  useEffect(() => {
    api.get('/erp/empresas').then(r => setEmpresas(r.data)).catch(() => {})
  }, [])

  async function buscarClientes(termo: string) {
    setBuscaCliente(termo)
    setClienteSel(null)
    if (termo.trim().length < 2) { setSugestoesCliente([]); return }
    const r = await api.get('/erp/clientes/lista', { params: { busca: termo, por_pagina: 10 } })
    setSugestoesCliente(r.data)
  }
  function selecionarCliente(c: { cli_cod: string; cli_nom: string; cli_fan: string }) {
    setClienteSel({ cli_cod: c.cli_cod, nome: c.cli_fan || c.cli_nom })
    setBuscaCliente(c.cli_fan || c.cli_nom)
    setSugestoesCliente([])
  }

  function filtrosParams() {
    return {
      dataIni, dataFim,
      empresas: empresasSel.join(','),
      cliCod: clienteSel?.cli_cod,
      tipos: tiposSel.join(','),
    }
  }

  async function gerar() {
    setCarregando(true)
    setErro('')
    setNotas(null)
    try {
      const r = await api.get('/relatorios/contas-receber', { params: filtrosParams() })
      setNotas(r.data)
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao gerar relatório.')
    } finally {
      setCarregando(false)
    }
  }

  async function gerarPdf() {
    setGerandoPdf(true)
    setErro('')
    try {
      const r = await api.get('/relatorios/contas-receber/pdf', { params: filtrosParams(), responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
    } catch (e: any) {
      setErro('Erro ao gerar PDF.')
    } finally {
      setGerandoPdf(false)
    }
  }

  async function gerarExcel() {
    setGerandoExcel(true)
    setErro('')
    try {
      const r = await api.get('/relatorios/contas-receber/excel', { params: filtrosParams(), responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
      const a = document.createElement('a')
      a.href = url
      a.download = 'relatorio-contas-receber.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    } catch (e: any) {
      setErro('Erro ao gerar Excel.')
    } finally {
      setGerandoExcel(false)
    }
  }

  const participantes = (() => {
    if (!notas) return []
    const mapa = new Map<string, { cli_cod: string; cliente: string; notas: Nota[]; totais: { valor: number; retido: number; financeiro: number; recebido: number } }>()
    for (const n of notas) {
      const atual = mapa.get(n.cli_cod) ?? { cli_cod: n.cli_cod, cliente: n.cliente, notas: [], totais: { valor: 0, retido: 0, financeiro: 0, recebido: 0 } }
      atual.notas.push(n)
      atual.totais.valor += Number(n.valor || 0)
      atual.totais.retido += Number(n.retido || 0)
      atual.totais.financeiro += Number(n.financeiro || 0)
      atual.totais.recebido += Number(n.recebido || 0)
      mapa.set(n.cli_cod, atual)
    }
    return Array.from(mapa.values())
  })()

  const totalGeral = (notas ?? []).reduce((acc, n) => {
    acc.valor += Number(n.valor || 0)
    acc.retido += Number(n.retido || 0)
    acc.financeiro += Number(n.financeiro || 0)
    acc.recebido += Number(n.recebido || 0)
    return acc
  }, { valor: 0, retido: 0, financeiro: 0, recebido: 0 })

  const th: React.CSSProperties = { padding: '0.55rem 0.7rem', textAlign: 'right', color: '#fff', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap' }
  const thLeft: React.CSSProperties = { ...th, textAlign: 'left' }
  const td: React.CSSProperties = { padding: '0.5rem 0.7rem', textAlign: 'right', color: '#374151', whiteSpace: 'nowrap' }
  const tdLeft: React.CSSProperties = { ...td, textAlign: 'left' }

  return (
    <div>
      <div className="card" style={{ marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1.1rem', color: '#1a6ea8' }}>
          Conferência Faturamento
        </h2>

        <div style={{ background: '#f7faff', borderLeft: '4px solid #1a6ea8', borderRadius: 8, padding: '0.9rem 1.1rem', marginBottom: '0.9rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.9rem', alignItems: 'flex-start' }}>
            <div className="field" style={{ width: 130, flex: '0 0 auto' }}>
              <label>Emissão de</label>
              <input type="date" value={dataIni} onChange={e => setDataIni(e.target.value)} />
            </div>
            <div className="field" style={{ width: 130, flex: '0 0 auto' }}>
              <label>Emissão até</label>
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
            </div>
            <ComboCheck
              label="Empresas"
              placeholder="Todas"
              width={200}
              opcoes={empresas.map(e => ({ value: String(e.empcod), label: `${e.empcod} — ${e.empfan || e.empnom}` }))}
              selecionados={empresasSel}
              onChange={setEmpresasSel}
            />
            <ComboCheck
              label="Tipo de NF"
              placeholder="Todos"
              width={210}
              opcoes={TIPOS}
              selecionados={tiposSel}
              onChange={setTiposSel}
            />
          </div>
        </div>

        <div style={{ background: '#fff8f2', borderLeft: '4px solid #f07820', borderRadius: 8, padding: '0.9rem 1.1rem', marginBottom: '0.9rem' }}>
          <div className="field" style={{ position: 'relative', width: 260, flex: '0 0 auto' }}>
            <label>Cliente</label>
            <input
              type="text"
              placeholder="Buscar por nome... (vazio = todos)"
              value={buscaCliente}
              onChange={e => buscarClientes(e.target.value)}
            />
            {sugestoesCliente.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
                maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}>
                {sugestoesCliente.map(c => (
                  <button key={c.cli_cod} type="button" onClick={() => selecionarCliente(c)}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.82rem' }}>
                    {c.cli_fan || c.cli_nom}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <p style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '1.2rem' }}>
          Traz apenas notas autorizadas e de saída.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>
            {carregando ? 'Gerando...' : 'Gerar relatório'}
          </button>
          {notas && (
            <button
              type="button"
              onClick={gerarPdf}
              disabled={gerandoPdf}
              style={{
                padding: '0.5rem 1rem', background: '#dc2626', color: '#fff', border: 'none',
                borderRadius: 6, fontWeight: 600, fontSize: '0.85rem', cursor: gerandoPdf ? 'not-allowed' : 'pointer',
                opacity: gerandoPdf ? 0.7 : 1,
              }}
            >
              {gerandoPdf ? 'Gerando PDF...' : 'PDF'}
            </button>
          )}
          {notas && (
            <button
              type="button"
              onClick={gerarExcel}
              disabled={gerandoExcel}
              style={{
                padding: '0.5rem 1rem', background: '#15803d', color: '#fff', border: 'none',
                borderRadius: 6, fontWeight: 600, fontSize: '0.85rem', cursor: gerandoExcel ? 'not-allowed' : 'pointer',
                opacity: gerandoExcel ? 0.7 : 1,
              }}
            >
              {gerandoExcel ? 'Gerando Excel...' : 'Excel'}
            </button>
          )}
        </div>
        {erro && <p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: 8 }}>{erro}</p>}
      </div>

      {notas && (
        <div className="card">
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1.2rem' }}>
            <div>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Faturado</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#111827' }}>R$ {fmtMoeda(totalGeral.valor)}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Retido</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#111827' }}>R$ {fmtMoeda(totalGeral.retido)}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Líquido</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#111827' }}>R$ {fmtMoeda(totalGeral.financeiro)}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Recebido</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#15803d' }}>R$ {fmtMoeda(totalGeral.recebido)}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>A Receber</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#dc2626' }}>R$ {fmtMoeda(totalGeral.financeiro - totalGeral.recebido)}</div>
            </div>
          </div>

          {participantes.map(part => (
            <div key={part.cli_cod} style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a6ea8', marginBottom: '0.6rem' }}>
                Participante → {part.cli_cod} - {part.cliente}
              </h3>
              <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ background: '#1a6ea8' }}>
                      <th style={thLeft}>Emissão</th>
                      <th style={thLeft}>Emp/NF/Série</th>
                      <th style={thLeft}>Fatura/Doc</th>
                      <th style={thLeft}>Parc./1º Vencto</th>
                      <th style={th}>Vlr. Nota</th>
                      <th style={th}>Vlr. Retido</th>
                      <th style={th}>Vlr. Financ.</th>
                      <th style={th}>Vlr. Recebido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {part.notas.map(n => (
                      <Fragment key={`${n.ntv_empcod}-${n.ntv_serie}-${n.ntv_numero}-${n.cli_cod}`}>
                        <tr style={{ background: '#fff', borderBottom: n.duplicatas?.length ? 'none' : '1px solid #eef0f4' }}>
                          <td style={tdLeft}>{fmtData(n.ntv_datemi)}</td>
                          <td style={tdLeft}>{n.ntv_empcod} / {n.ntv_numero} / {n.ntv_serie}</td>
                          <td style={tdLeft}>{n.ntv_numero} {n.ftv_tipdco}</td>
                          <td style={tdLeft}>{n.qtd_parcelas} / {fmtData(n.primeiro_vencto)}</td>
                          <td style={td}>{fmtMoeda(n.valor)}</td>
                          <td style={td}>{fmtMoeda(n.retido)}</td>
                          <td style={td}>{fmtMoeda(n.financeiro)}</td>
                          <td style={{ ...td, color: Number(n.recebido) > 0 ? '#15803d' : '#374151', fontWeight: Number(n.recebido) > 0 ? 600 : 400 }}>
                            {fmtMoeda(n.recebido)}
                          </td>
                        </tr>
                        {n.duplicatas?.length ? (
                          <tr style={{ borderBottom: '1px solid #eef0f4' }}>
                            <td></td>
                            <td colSpan={7} style={{ padding: '0.3rem 0.7rem 0.6rem' }}>
                              <div style={{ background: '#f7faff', borderRadius: 6, padding: '0.5rem 0.7rem' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                                  <thead>
                                    <tr style={{ color: '#1a6ea8' }}>
                                      <th style={{ textAlign: 'left', padding: '0.2rem 0.5rem' }}>Dup</th>
                                      <th style={{ textAlign: 'left', padding: '0.2rem 0.5rem' }}>Vencimento</th>
                                      <th style={{ textAlign: 'right', padding: '0.2rem 0.5rem' }}>Vlr. Líquido</th>
                                      <th style={{ textAlign: 'right', padding: '0.2rem 0.5rem' }}>Recebido</th>
                                      <th style={{ textAlign: 'right', padding: '0.2rem 0.5rem' }}>Saldo</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {n.duplicatas.map(d => (
                                      <Fragment key={d.dpv_numero}>
                                        <tr>
                                          <td style={{ padding: '0.2rem 0.5rem' }}>{d.dpv_numero}</td>
                                          <td style={{ padding: '0.2rem 0.5rem' }}>{fmtData(d.vencimento)}</td>
                                          <td style={{ padding: '0.2rem 0.5rem', textAlign: 'right' }}>{fmtMoeda(d.valor_liquido)}</td>
                                          <td style={{ padding: '0.2rem 0.5rem', textAlign: 'right' }}>{fmtMoeda(d.recebido)}</td>
                                          <td style={{ padding: '0.2rem 0.5rem', textAlign: 'right' }}>{fmtMoeda(d.saldo)}</td>
                                        </tr>
                                        {d.pagamentos.map((pg, i) => (
                                          <tr key={i} style={{ color: '#6b7280', fontStyle: 'italic' }}>
                                            <td></td>
                                            <td colSpan={4} style={{ padding: '0.1rem 0.5rem' }}>
                                              Data: {fmtData(pg.data)}  Portad: {pg.portador_cod} - {pg.portador_des}  Recebido: {fmtMoeda(pg.valor)}
                                            </td>
                                          </tr>
                                        ))}
                                      </Fragment>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid #1a6ea8', background: '#f7faff', fontWeight: 700 }}>
                      <td colSpan={4} style={{ padding: '0.6rem 0.7rem', textAlign: 'right', color: '#1a6ea8' }}>Totais do Participante:</td>
                      <td style={{ ...td, fontWeight: 700 }}>{fmtMoeda(part.totais.valor)}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{fmtMoeda(part.totais.retido)}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{fmtMoeda(part.totais.financeiro)}</td>
                      <td style={{ ...td, color: '#15803d', fontWeight: 700 }}>{fmtMoeda(part.totais.recebido)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
